/*!
 * tapeClock.js — where a sound played elsewhere is, from its reports
 * © 2026 Rob Canning — GPLv3
 *
 * A file played by an external engine (SuperCollider, Pd, Max …: see
 * externalTape.js) reports its position a few times a second:
 * /<uid>/pos <sec> <rate>. The cursor on its waveform is drawn every frame
 * from those reports:
 *
 *   - between reports it runs on from the last one at the reported rate,
 *     so twenty reports a second draw as smoothly as sixty;
 *   - a report that disagrees with where the cursor has got to by a little
 *     (jitter) is eased in over `slewMs`, not jumped to -- no wobble;
 *   - by more than `jumpSec` (a seek, a restart) it is jumped to;
 *   - with no report for `stallMs` the cursor stops where it is and says
 *     so (stalled), rather than run on past a sound that has stopped.
 *
 * Each report is placed at the moment it was made, as near as can be
 * known: the caller passes `at`, a local time -- the server's arrival
 * stamp turned into this browser's clock (serverClock.js toLocal), not
 * when the browser happened to get it, which bundling and Wi-Fi shift by
 * tens of ms.
 *
 * Pure: times in ms are passed in, so it can be tested without a browser.
 */

export function createTapeClock({ slewMs = 50, jumpSec = 0.1, stallMs = 250 } = {}) {
  let anchorSec = 0;          // position at anchorAt, before the slew
  let anchorAt = null;        // local ms
  let rate = 0;
  let slewErr = 0;            // eased in over slewMs from anchorAt
  let lastAt = null;          // when the last report was made (local ms)
  let reports = 0;
  let ignoreBefore = -Infinity;   // reports made before a seek took effect

  const raw = (t) => {
    if (anchorAt == null) return 0;
    const dt = t - anchorAt;
    const slew = slewMs > 0 ? Math.min(1, Math.max(0, dt / slewMs)) : 1;
    return anchorSec + (dt / 1000) * rate + slewErr * slew;
  };

  return {
    /** A report: the engine was at `sec`, playing at `rate`, at local time `at`. */
    report(sec, r, at) {
      if (!Number.isFinite(sec) || !Number.isFinite(at) || at < ignoreBefore) return;
      const shown = raw(at);
      const err = sec - shown;
      if (anchorAt == null || Math.abs(err) > jumpSec) {
        anchorSec = sec; slewErr = 0;
      } else {
        anchorSec = shown; slewErr = err;
      }
      anchorAt = at;
      rate = Number.isFinite(r) ? r : rate;
      lastAt = Math.max(lastAt ?? at, at);
      reports++;
    },

    /** Where to draw at local time `t`: { sec, stalled, live }. */
    at(t) {
      if (anchorAt == null) return { sec: 0, stalled: false, live: false };
      const stalled = t - lastAt > stallMs;
      // stalled: held where the last report would have carried it by now
      return { sec: raw(stalled ? lastAt + stallMs : t), stalled, live: true };
    },

    /**
     * A seek was sent: from `at` the engine will be at `sec`. Reports made
     * before then describe where it was and are ignored -- one still in
     * flight looked like the seek had failed, and was seeked again.
     */
    seekTo(sec, at) {
      anchorSec = sec; anchorAt = at; slewErr = 0;
      ignoreBefore = at;
      lastAt = Math.max(lastAt ?? at, at);
    },

    /** Forget everything (stopped). */
    reset() { anchorAt = null; lastAt = null; slewErr = 0; rate = 0; reports = 0; ignoreBefore = -Infinity; },

    get reports() { return reports; },
    get rate() { return rate; },
  };
}

/**
 * Keeping a tape on the score (externalTape.js lock): what to do about
 * `err`, seconds the tape is behind where the playhead says it should be
 * (negative: ahead).
 *
 *   - a big gap is seeked away: over SNAP_SEC once in the first second
 *     (the start-up delay -- the play message's trip, the engine starting
 *     the sound -- put right at once, where a jump is not heard; once:
 *     each seek is a small jump, and what one leaves the rate plays out),
 *     over JUMP_SEC otherwise (a stall, a seek the engine missed); not
 *     twice within SEEK_GAP_MS, so the engine has time to act on the first;
 *   - a small one is played out by nudging the rate, at most NUDGE_MAX
 *     either way (0.5 %: under 9 cents, not heard), proportionally: 10 ms
 *     behind plays 0.5 % fast and is gone in two seconds.
 *
 * Returns { seek: boolean, rate } (rate = base when nothing needs doing).
 */
export const LOCK = { SNAP_SEC: 0.008, JUMP_SEC: 0.05, SEEK_GAP_MS: 400, NUDGE_MAX: 0.005, GAIN: 0.5, START_MS: 1000 };

export function lockStep({ err, base = 1, sinceStartMs = Infinity, sinceSeekMs = Infinity, snapped = false }) {
  const limit = !snapped && sinceStartMs < LOCK.START_MS ? LOCK.SNAP_SEC : LOCK.JUMP_SEC;
  if (Math.abs(err) > limit && sinceSeekMs > LOCK.SEEK_GAP_MS) return { seek: true, rate: base };
  const nudge = Math.max(-LOCK.NUDGE_MAX, Math.min(LOCK.NUDGE_MAX, err * LOCK.GAIN));
  return { seek: false, rate: base * (1 + nudge) };
}
